import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PasswordVisibilityToggle } from "@/components/PasswordVisibilityToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Shield, AlertTriangle } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { useLocale } from "@/i18n/context";
import { dashboardPath, localizedPath } from "@/i18n/routes";
import { getAuthRedirectUrl } from "@/lib/authRedirect";
import { signInWithPasswordCompat } from "@/lib/authPassword";
import { subscribeToNewsletter } from "@/lib/newsletter";
import { trackRegistration } from "@/lib/metaPixel";

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAuthSuccess?: () => void;
  defaultMode?: 'login' | 'register';
  /**
   * Where login, signup verification and OAuth return. Defaults to the
   * dashboard in the visitor's language. Null keeps a password login on the
   * current page; email/OAuth then return to that dashboard instead.
   */
  redirectTo?: string | null;
}

export const AuthDialog = ({
  open,
  onOpenChange,
  onAuthSuccess,
  defaultMode = 'login',
  redirectTo,
}: AuthDialogProps) => {
  const [isLogin, setIsLogin] = useState(defaultMode === 'login');

  // Reset mode when defaultMode prop changes
  useEffect(() => {
    setIsLogin(defaultMode === 'login');
  }, [defaultMode, open]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [displayNameError, setDisplayNameError] = useState("");
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [securityWarning, setSecurityWarning] = useState("");
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t, locale } = useLocale();
  // Never fall back to the unprefixed (Slovenian) dashboard: an /en, /de or
  // /ru visitor would land on a Slovenian page after logging in.
  const localDashboard = dashboardPath(locale);
  const navigateTo = redirectTo === undefined ? localDashboard : redirectTo;
  const returnUrl = getAuthRedirectUrl(navigateTo ?? localDashboard);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): { isValid: boolean; message: string } => {
    if (password.length < 8) {
      return { isValid: false, message: t.auth.passwordTooShort };
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return { isValid: false, message: t.auth.passwordNeedsLower };
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return { isValid: false, message: t.auth.passwordNeedsUpper };
    }
    if (!/(?=.*\d)/.test(password)) {
      return { isValid: false, message: t.auth.passwordNeedsDigit };
    }
    return { isValid: true, message: "" };
  };

  // Input sanitization
  const sanitizeInput = (input: string): string => {
    return input.trim().replace(/[<>"'&]/g, '');
  };

  const handleEmailChange = (value: string) => {
    const sanitized = sanitizeInput(value);
    setEmail(sanitized);
    
    if (sanitized && !validateEmail(sanitized)) {
      setEmailError(t.auth.invalidEmail);
    } else {
      setEmailError("");
    }
  };

  // Passwords are never "sanitized": stripping characters here while the
  // recovery dialog stores them raw locked people out (see lib/authPassword).
  const handlePasswordChange = (value: string) => {
    setPassword(value);
    
    if (!isLogin && value) {
      const validation = validatePassword(value);
      setPasswordError(validation.message);
    } else {
      setPasswordError("");
    }
  };

  const handleDisplayNameChange = (value: string) => {
    const sanitized = sanitizeInput(value);
    setDisplayName(sanitized);
    
    if (!isLogin && sanitized.length < 2) {
      setDisplayNameError(t.auth.displayNameTooShort);
    } else {
      setDisplayNameError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSecurityWarning("");
    
    // Validate inputs
    if (!validateEmail(email)) {
      setEmailError(t.auth.invalidEmail);
      return;
    }
    
    if (!isLogin) {
      if (!displayName || displayName.length < 2) {
        setDisplayNameError(t.auth.displayNameTooShort);
        return;
      }
      
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        setPasswordError(passwordValidation.message);
        return;
      }
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await signInWithPasswordCompat(
          (credentials) => supabase.auth.signInWithPassword(credentials),
          email.toLowerCase(),
          password,
        );
        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            throw new Error(t.auth.invalidCredentials);
          }
          if (error.message.includes("Email not confirmed")) {
            const { error: resendError } = await supabase.auth.resend({
              type: "signup",
              email: email.toLowerCase(),
              options: { emailRedirectTo: returnUrl },
            });
            if (resendError) {
              throw new Error(t.auth.resendConfirmationFailed);
            }
            toast({
              title: t.auth.resendConfirmationTitle,
              description: t.auth.resendConfirmationSent,
            });
            return;
          }
          throw error;
        }
        if (navigateTo) navigate(navigateTo);
      } else {
        const { data: signUpData, error } = await supabase.auth.signUp({
          email: email.toLowerCase(),
          password,
          options: {
            emailRedirectTo: returnUrl,
            data: {
              full_name: displayName,
              display_name: displayName,
            }
          }
        });
        if (error) {
          if (error.message.includes("User already registered")) {
            throw new Error(t.auth.userExists);
          }
          if (error.message.includes("weak_password") || error.message.includes("Password is known to be weak")) {
            throw new Error(t.auth.weakPassword);
          }
          throw error;
        }
        
        // The welcome email is sent by the on_auth_user_created_send_welcome
        // trigger, not from here — send-email rejects an unprivileged 'welcome'.
        const isNewSignup = (signUpData.user?.identities?.length ?? 0) > 0;
        if (isNewSignup && signUpData.user) {
          trackRegistration(signUpData.user.id, 'email');

          // Best-effort: a newsletter hiccup must never look like a failed signup.
          if (newsletterOptIn) {
            try {
              await subscribeToNewsletter({
                source: 'signup',
                email: email.toLowerCase(),
                name: displayName || undefined,
              });
            } catch (newsletterError) {
              console.warn('Failed to subscribe to newsletter:', newsletterError);
            }
          }
        }
        
        toast({
          title: t.auth.registerSuccess,
          description: t.auth.registerSuccessDesc,
        });
      }
      
      onOpenChange(false);
      onAuthSuccess?.();
    } catch (caught) {
      const error = caught as Error;
      console.error('Auth error:', error);
      
      // Check for suspicious activity
      if (error.message.includes("rate limit") || error.message.includes("too many")) {
        setSecurityWarning(t.auth.suspiciousActivity);
      }
      
      toast({
        title: t.common.error,
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!validateEmail(email)) {
      setEmailError(t.auth.invalidEmail);
      toast({
        title: t.common.error,
        description: t.auth.resetEnterEmail,
        variant: "destructive",
      });
      return;
    }

    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.toLowerCase(), {
        // `reset=1` tells PasswordRecoveryDialog to prompt for a new password.
        redirectTo: getAuthRedirectUrl(`${localizedPath("home", locale)}?reset=1`),
      });
      if (error) throw error;
      toast({
        title: t.auth.resetSentTitle,
        description: t.auth.resetSentDesc,
      });
    } catch (error: unknown) {
      console.error("Password reset error:", error);
      toast({
        title: t.common.error,
        description: error instanceof Error ? error.message : t.auth.resetError,
        variant: "destructive",
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setSecurityWarning("");

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: returnUrl,
          skipBrowserRedirect: true,
        }
      });

      if (error) {
        throw error;
      }

      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
    } catch (error) {
      console.error('Google auth error:', error);
      
      toast({
        title: t.common.error,
        description: t.auth.googleError,
        variant: "destructive",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    setFacebookLoading(true);
    setSecurityWarning("");

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: returnUrl,
          skipBrowserRedirect: true,
        }
      });

      if (error) {
        throw error;
      }

      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
    } catch (error) {
      console.error('Facebook auth error:', error);
      
      toast({
        title: t.common.error,
        description: t.auth.facebookError,
        variant: "destructive",
      });
    } finally {
      setFacebookLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-modal="true" className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-md border-border/60 shadow-xl">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-2xl font-bold tracking-tight">
            {isLogin ? t.auth.login : t.auth.register}
          </DialogTitle>
          <DialogDescription>
            {isLogin ? t.auth.loginDesc : t.auth.registerDesc}
          </DialogDescription>
        </DialogHeader>
        
        {securityWarning && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{securityWarning}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="displayName">{t.auth.displayName}</Label>
              <Input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => handleDisplayNameChange(e.target.value)}
                required={!isLogin}
                placeholder={t.auth.displayNamePlaceholder}
                autoComplete="name"
                aria-invalid={Boolean(displayNameError)}
                aria-describedby={displayNameError ? "auth-displayname-error" : undefined}
                className={displayNameError ? "border-destructive" : ""}
              />
              {displayNameError && (
                <p id="auth-displayname-error" role="alert" className="text-sm text-destructive">
                  {displayNameError}
                </p>
              )}
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email">{t.auth.email}</Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                required
                autoComplete="email"
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? "auth-email-error" : undefined}
                className={emailError ? "border-destructive" : ""}
              />
              <Shield className="absolute right-3 top-3 h-4 w-4 text-muted-foreground" />
            </div>
            {emailError && (
              <p id="auth-email-error" role="alert" className="text-sm text-destructive">
                {emailError}
              </p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">{t.auth.password}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                required
                minLength={8}
                autoComplete={isLogin ? "current-password" : "new-password"}
                aria-invalid={Boolean(passwordError)}
                aria-describedby={passwordError ? "auth-password-error" : undefined}
                className={passwordError ? "border-destructive" : ""}
              />
              <PasswordVisibilityToggle
                visible={showPassword}
                onToggle={() => setShowPassword(!showPassword)}
                controls="password"
              />
            </div>
            {passwordError && (
              <p id="auth-password-error" role="alert" className="text-sm text-destructive">
                {passwordError}
              </p>
            )}
            {!isLogin && (
              <p className="text-xs text-muted-foreground">
                {t.auth.passwordHint}
              </p>
            )}
            {isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={resetLoading}
                  className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
                >
                  {resetLoading ? t.common.sending : t.auth.forgotPassword}
                </button>
              </div>
            )}
          </div>

          {!isLogin && (
            <div className="flex items-start gap-2.5">
              <Checkbox
                id="newsletter-optin"
                checked={newsletterOptIn}
                onCheckedChange={(checked) => setNewsletterOptIn(checked === true)}
                className="mt-0.5"
              />
              <Label
                htmlFor="newsletter-optin"
                className="text-sm font-normal leading-snug text-muted-foreground"
              >
                {t.newsletter.optIn}
              </Label>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isLogin ? t.auth.loginSubmit : t.auth.registerSubmit}
          </Button>

          {/* Sits under the submit button so the terms are presented before the
              account is created, not after. A notice rather than a checkbox:
              registration is free and processes no data beyond what running the
              account requires, so the binding consent that needs an active tick
              is the one at checkout, not this one. */}
          {!isLogin && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t.auth.legalNotice}{" "}
              <Link
                to={localizedPath("terms", locale)}
                className="text-primary underline underline-offset-2"
                onClick={() => onOpenChange(false)}
              >
                {t.auth.legalTerms}
              </Link>{" "}
              {t.common.and}{" "}
              <Link
                to={localizedPath("privacy", locale)}
                className="text-primary underline underline-offset-2"
                onClick={() => onOpenChange(false)}
              >
                {t.auth.legalPrivacy}
              </Link>
              .
            </p>
          )}
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t.common.or}
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
          >
            {googleLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {googleLoading ? t.auth.redirectingToGoogle : (isLogin ? t.auth.loginWithGoogle : t.auth.registerWithGoogle)}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleFacebookLogin}
            disabled={facebookLoading}
          >
            {facebookLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="#1877F2"
                d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
              />
            </svg>
            {isLogin ? t.auth.loginWithFacebook : t.auth.registerWithFacebook}
          </Button>
        </div>

        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={() => setIsLogin(!isLogin)}
        >
          {isLogin ? t.auth.noAccount : t.auth.hasAccount}
        </Button>
      </DialogContent>
    </Dialog>
  );
};